import { useEffect, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Upload,
  Check,
  Loader2,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Users,
  ClipboardCheck,
  Hammer,
  LifeBuoy,
  Wallet,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { Reveal } from "@/components/site/Reveal";
import { BRAND } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1542317854-23ed72d6d384?auto=format&fit=crop&w=2400&q=85";

const SITE_IMG =
  "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=2000&q=85";

const CONTACT_CARDS = [
  {
    icon: Phone,
    label: "Call Us",
    value: BRAND.phone,
    href: BRAND.phoneHref,
    desc: "Speak directly with our project consultants.",
  },
  {
    icon: Mail,
    label: "Email",
    value: BRAND.email,
    href: BRAND.emailHref,
    desc: "Send us your requirements and receive a detailed quotation.",
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: BRAND.whatsapp,
    href: BRAND.whatsappHref,
    desc: "Chat instantly. Share drawings, photos or project details.",
  },
  {
    icon: MapPin,
    label: "Office Location",
    value: "Dubai, United Arab Emirates",
    href: "https://maps.google.com/?q=Business+Bay+Dubai",
    desc: "Serving residential and commercial projects across Dubai.",
  },
];

const SERVICES_OPTIONS = [
  "Interior Fit-Out",
  "Renovation",
  "Holiday Home Furnishing",
  "MEP Services",
  "Facility Maintenance",
  "Professional Cleaning",
  "Authority Approvals",
  "Waterproofing",
  "Annual Maintenance Contract",
];

const PROPERTY_TYPES = ["Apartment", "Villa", "Office", "Retail", "Restaurant", "Hotel", "Warehouse", "Holiday Home"];
const PROJECT_SIZES = ["Small", "Medium", "Large"];
const BUDGETS = ["AED 5K - 20K", "AED 20K - 50K", "AED 50K - 100K", "AED 100K+"];
const TIMELINES = ["Immediately", "Within 1 Month", "Within 3 Months", "Planning Stage"];

const WHY = [
  { icon: Users, label: "One Team" },
  { icon: ShieldCheck, label: "Turnkey Solutions" },
  { icon: Hammer, label: "Experienced Professionals" },
  { icon: Wallet, label: "Transparent Pricing" },
  { icon: ClipboardCheck, label: "Premium Materials" },
  { icon: LifeBuoy, label: "Long-Term Support" },
];

const RESPONSE_STEPS = [
  "Submit Your Enquiry",
  "Our Team Contacts You",
  "Schedule Site Visit",
  "Receive Detailed Quotation",
  "Project Execution",
  "Quality Handover",
  "After Sales Support",
];

const SERVICE_AREAS = [
  "Downtown Dubai", "Dubai Marina", "Business Bay", "Palm Jumeirah", "JVC", "JVT",
  "Arabian Ranches", "Dubai Hills", "Jumeirah", "Al Barsha", "Meydan",
  "Dubai Creek Harbour", "DAMAC Hills", "Dubai South", "Silicon Oasis",
  "Mirdif", "Al Furjan", "Town Square", "Bluewaters", "Emaar Beachfront",
];

const FAQS = [
  { q: "How soon can I receive a quotation?", a: "Most quotations are delivered within 24 to 48 hours of the site visit. Smaller scopes (cleaning, single repairs) can be quoted same day via WhatsApp." },
  { q: "Is the site visit free?", a: "Yes. Site visits across Dubai are complimentary and carry no obligation. Our consultant will inspect, measure and advise before preparing a written proposal." },
  { q: "Do you provide emergency maintenance?", a: "Yes. We offer emergency response across Dubai for leaks, electrical failures, AC breakdowns and lockouts — typically dispatched within hours." },
  { q: "Do you work on weekends?", a: "Yes. Our cleaning, maintenance and holiday-home teams operate 7 days a week. Fit-out works can also be scheduled on weekends when required." },
  { q: "Can you handle authority approvals?", a: "Yes. We coordinate Dubai Municipality, DEWA, DDA, DCD, Trakhees, Nakheel, Emaar and Dubai South approvals end-to-end on behalf of our clients." },
  { q: "Do you provide materials?", a: "Yes. Bloc & Brush supplies all materials, fixtures and finishes. Material samples are presented and approved before procurement." },
  { q: "How long do projects take?", a: "Apartment fit-outs typically range from 4–10 weeks. Villas and commercial projects vary based on scope. Cleaning jobs are usually completed within a single day." },
  { q: "Do you provide warranty?", a: "Yes. Fit-out, MEP and waterproofing works include a written workmanship warranty. Specific durations are outlined in your project contract." },
  { q: "Can I request only cleaning services?", a: "Absolutely. Cleaning is offered as a standalone service with fixed transparent pricing — no minimum project size required." },
  { q: "Do you undertake commercial projects?", a: "Yes. We deliver fit-out, MEP and maintenance for offices, restaurants, retail, clinics, hotels and developer handover packages." },
];

export default function Contact() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    whatsapp: "",
    location: "",
    service: "",
    property_type: "",
    project_size: "",
    budget: "",
    timeline: "",
    message: "",
  });

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const heroScale = useTransform(scrollY, [0, 600], [1.05, 1.15]);

  useEffect(() => {
    document.title = "Contact Bloc & Brush | Interior Fit-Out & Cleaning Services Dubai";
    window.scrollTo(0, 0);
  }, []);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []);
    const valid = list.filter((f) => f.size <= 20 * 1024 * 1024);
    if (valid.length !== list.length) {
      toast.error("Some files exceeded the 20MB limit and were skipped.");
    }
    setFiles(valid);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const attachments = files.length
        ? `\n\nAttachments: ${files.map((f) => f.name).join(", ")}`
        : "";
      const messageWithExtras = [
        form.company ? `Company: ${form.company}` : null,
        form.whatsapp ? `WhatsApp: ${form.whatsapp}` : null,
        form.property_type ? `Property Type: ${form.property_type}` : null,
        form.project_size ? `Project Size: ${form.project_size}` : null,
        form.budget ? `Budget: ${form.budget}` : null,
        form.timeline ? `Timeline: ${form.timeline}` : null,
        form.message ? `\nMessage:\n${form.message}` : null,
      ]
        .filter(Boolean)
        .join("\n") + attachments;

      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service || null,
        project_type: form.property_type || null,
        location: form.location || null,
        message: messageWithExtras,
        source: "contact-page",
      });
      setSuccess(true);
      toast.success("Enquiry sent! Our team will respond within 24 hours.");
      setTimeout(() => {
        setForm({
          name: "", company: "", email: "", phone: "", whatsapp: "",
          location: "", service: "", property_type: "", project_size: "",
          budget: "", timeline: "", message: "",
        });
        setFiles([]);
        setSuccess(false);
      }, 4000);
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* HERO */}
      <section data-testid="contact-hero" className="relative min-h-[88vh] flex items-end overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Luxury Dubai skyline and architecture" className="w-full h-[120%] object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A33]/25 via-[#0B1A33]/25 to-[#0B1A33]/80" />

        <div className="relative container mx-auto px-6 lg:px-12 max-w-7xl pt-32 pb-20 lg:pb-28 text-white">
          <motion.span initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-xs uppercase tracking-[0.28em] text-white/70">
            Contact Us
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }} data-testid="contact-hero-title" className="font-display mt-6 text-5xl md:text-6xl lg:text-[72px] font-extrabold leading-[0.98] tracking-tight max-w-5xl">
            Let's Build Something
            <br />
            <span className="text-white/65">Exceptional Together.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }} className="mt-8 text-base lg:text-[18px] text-white/75 max-w-2xl leading-relaxed">
            Whether you're planning an interior fit-out, renovation, holiday
            home furnishing, maintenance or professional cleaning service, our
            experienced team is ready to help. We're only one conversation away.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.45 }} className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a href="#contact-form" data-testid="contact-hero-quote-btn" className="inline-flex items-center justify-center gap-2 bg-white text-[#13294B] hover:bg-white/90 rounded-full px-7 py-4 text-[15px] font-medium transition">
              Request Free Quote <ArrowRight size={16} />
            </a>
            <a href={BRAND.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="contact-hero-whatsapp-btn" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] rounded-full px-7 py-4 text-[15px] font-medium transition">
              <MessageCircle size={16} /> WhatsApp Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="py-24 lg:py-32 bg-white" data-testid="contact-channels">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Get in touch</span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                Speak With <span className="text-[#13294B]">Our Team.</span>
              </h2>
              <p className="mt-6 text-[17px] leading-[1.8] text-[#1C1C1C]/70">
                Every successful project begins with a conversation. Whether you
                have a complete design brief or simply need professional advice,
                our experienced team will guide you through every stage — from
                site visits and quotations to project execution and long-term
                maintenance.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CONTACT_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.label} delay={(i % 4) * 0.05}>
                  <a
                    href={c.href}
                    target={c.label === "WhatsApp" || c.label === "Office Location" ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={`contact-card-${c.label.toLowerCase().replace(/ /g, "-")}`}
                    className="group block h-full bg-[#F5F5F5] hover:bg-[#13294B] rounded-2xl p-7 lg:p-8 transition-colors duration-500"
                  >
                    <div className="w-11 h-11 rounded-xl bg-white text-[#13294B] flex items-center justify-center group-hover:bg-white/15 group-hover:text-white transition-colors">
                      <Icon size={20} strokeWidth={1.6} />
                    </div>
                    <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-[#707070] group-hover:text-white/55 transition-colors">
                      {c.label}
                    </div>
                    <div className="mt-2 font-display text-[17px] font-bold text-[#1C1C1C] group-hover:text-white transition-colors break-all">
                      {c.value}
                    </div>
                    <p className="mt-3 text-[13px] leading-relaxed text-[#1C1C1C]/65 group-hover:text-white/65 transition-colors">
                      {c.desc}
                    </p>
                  </a>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="contact-form" className="py-24 lg:py-32 bg-[#F5F5F5]" data-testid="contact-form-section">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Free Consultation</span>
            <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
              Request a Free <span className="text-[#13294B]">Consultation.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 bg-white rounded-3xl p-8 lg:p-12 shadow-[0_20px_60px_rgba(19,41,75,0.08)]">
              {success ? (
                <div className="flex flex-col items-center text-center py-16">
                  <CheckCircle2 size={56} className="text-[#13294B]" />
                  <h3 className="font-display text-2xl font-bold mt-5">Enquiry received</h3>
                  <p className="text-[#707070] mt-2 max-w-md">
                    Our project consultant will reach out within 24 hours. For urgent matters, please WhatsApp us directly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Full Name *">
                      <Input data-testid="contact-name" value={form.name} onChange={update("name")} required className="h-11" />
                    </Field>
                    <Field label="Company Name (Optional)">
                      <Input data-testid="contact-company" value={form.company} onChange={update("company")} className="h-11" />
                    </Field>
                    <Field label="Email Address *">
                      <Input type="email" data-testid="contact-email" value={form.email} onChange={update("email")} required className="h-11" />
                    </Field>
                    <Field label="Phone Number *">
                      <Input data-testid="contact-phone" value={form.phone} onChange={update("phone")} required className="h-11" />
                    </Field>
                    <Field label="WhatsApp Number">
                      <Input data-testid="contact-whatsapp" value={form.whatsapp} onChange={update("whatsapp")} className="h-11" />
                    </Field>
                    <Field label="Property Location">
                      <Input data-testid="contact-location" placeholder="e.g. Downtown Dubai" value={form.location} onChange={update("location")} className="h-11" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Select Service">
                      <SelectField value={form.service} onChange={update("service")} options={SERVICES_OPTIONS} placeholder="Choose a service" testid="contact-service" />
                    </Field>
                    <Field label="Property Type">
                      <SelectField value={form.property_type} onChange={update("property_type")} options={PROPERTY_TYPES} placeholder="Select type" testid="contact-property-type" />
                    </Field>
                    <Field label="Project Size">
                      <SelectField value={form.project_size} onChange={update("project_size")} options={PROJECT_SIZES} placeholder="Select size" testid="contact-project-size" />
                    </Field>
                    <Field label="Project Budget">
                      <SelectField value={form.budget} onChange={update("budget")} options={BUDGETS} placeholder="Select budget" testid="contact-budget" />
                    </Field>
                  </div>

                  <Field label="Timeline">
                    <SelectField value={form.timeline} onChange={update("timeline")} options={TIMELINES} placeholder="When do you plan to start?" testid="contact-timeline" />
                  </Field>

                  <Field label="Message">
                    <Textarea rows={5} data-testid="contact-message" value={form.message} onChange={update("message")} placeholder="Share your project brief, requirements or questions." />
                  </Field>

                  <Field label="Attachments (Floor Plans · Drawings · Reference Images · BOQ · PDF — max 20MB)">
                    <label
                      htmlFor="contact-files"
                      className="flex flex-col items-center justify-center gap-2 border border-dashed border-[#13294B]/25 hover:border-[#13294B] hover:bg-[#13294B]/5 rounded-2xl py-8 cursor-pointer transition-colors"
                    >
                      <Upload size={22} className="text-[#13294B]" />
                      <span className="text-[14px] text-[#1C1C1C]/70">
                        {files.length > 0
                          ? files.map((f) => f.name).join(", ")
                          : "Click to upload or drag files here"}
                      </span>
                    </label>
                    <input id="contact-files" data-testid="contact-files" type="file" multiple onChange={onFiles} className="hidden" accept=".pdf,.png,.jpg,.jpeg,.dwg,.doc,.docx,.xls,.xlsx" />
                  </Field>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-[#13294B] hover:bg-[#0E1F3A] text-white rounded-full py-4 font-medium text-[15px] transition flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    {submitting ? "Sending…" : "Request Free Quote"}
                    {!submitting && <ArrowRight size={16} />}
                  </button>

                  <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[13px] text-[#1C1C1C]/65">
                    <span className="flex items-center gap-2"><Check size={14} className="text-[#13294B]" /> Free Site Visit</span>
                    <span className="flex items-center gap-2"><Check size={14} className="text-[#13294B]" /> No-Obligation Consultation</span>
                    <span className="flex items-center gap-2"><Check size={14} className="text-[#13294B]" /> Fast Response</span>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* BOOK SITE VISIT */}
      <section className="relative overflow-hidden" data-testid="contact-site-visit">
        <div className="absolute inset-0">
          <img src={SITE_IMG} alt="Engineer inspecting luxury Dubai apartment" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1A33] via-[#0B1A33]/85 to-[#0B1A33]/30" />
        <div className="relative container mx-auto px-6 lg:px-12 max-w-7xl py-24 lg:py-32 text-white">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.22em] text-white/55">Site Inspection</span>
            <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight">
              Book a Free
              <br />
              <span className="text-white/65">Site Inspection.</span>
            </h2>
            <p className="mt-6 text-[17px] leading-[1.75] text-white/75 max-w-xl">
              Our specialists will visit your property, understand your
              requirements, evaluate the site and recommend the most suitable
              solutions before preparing a detailed quotation.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[15px]">
              {["Professional Advice", "Accurate Measurements", "Transparent Pricing", "Project Planning"].map((b) => (
                <li key={b} className="flex items-center gap-2 text-white/85">
                  <Check size={16} className="text-white" /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <a href="#contact-form" data-testid="schedule-site-visit" className="inline-flex items-center gap-2 bg-white text-[#13294B] hover:bg-white/90 rounded-full px-7 py-4 text-[15px] font-medium transition">
                Schedule Site Visit <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CONTACT */}
      <section className="py-24 lg:py-32 bg-white" data-testid="contact-why">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Why Bloc & Brush</span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                Six Reasons To Speak
                <br />
                <span className="text-[#13294B]">With Us Today.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-5">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.label} delay={(i % 3) * 0.05}>
                  <div className="group h-full bg-[#F5F5F5] hover:bg-[#13294B] rounded-2xl p-8 transition-colors duration-500">
                    <Icon size={22} strokeWidth={1.6} className="text-[#13294B] group-hover:text-white transition-colors" />
                    <div className="font-display mt-7 text-[17px] font-bold text-[#1C1C1C] group-hover:text-white transition-colors">
                      {w.label}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* RESPONSE PROCESS */}
      <section className="py-24 lg:py-32 bg-[#0B1A33] text-white relative overflow-hidden" data-testid="contact-process">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-white/55">Response Process</span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight">
                From Enquiry
                <br />
                <span className="text-white/65">To Aftercare.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-16 relative">
            <div className="hidden lg:block absolute top-[14px] left-0 right-0 h-px bg-white/15" />
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-y-10 gap-x-5">
              {RESPONSE_STEPS.map((s, i) => (
                <Reveal key={s} delay={i * 0.05}>
                  <div>
                    <div className="w-7 h-7 rounded-full bg-white text-[#13294B] text-xs font-bold flex items-center justify-center">{i + 1}</div>
                    <h3 className="font-display mt-5 text-[14px] lg:text-[15px] font-bold tracking-tight">{s}</h3>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-24 lg:py-32 bg-white" data-testid="contact-areas">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Service Areas</span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                Serving Clients
                <br />
                <span className="text-[#13294B]">Across Dubai.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 flex flex-wrap gap-2.5">
            {SERVICE_AREAS.map((a) => (
              <span key={a} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F5F5F5] text-[#1C1C1C] text-[13px] font-medium hover:bg-[#13294B] hover:text-white transition-colors cursor-default">
                <MapPin size={12} className="opacity-70" /> {a}
              </span>
            ))}
          </div>
          <p className="mt-6 text-[14px] text-[#707070]">
            We also serve surrounding Emirates for selected projects.
          </p>

          {/* GOOGLE MAP */}
          <div className="mt-14 rounded-3xl overflow-hidden border border-black/5 shadow-[0_20px_60px_rgba(19,41,75,0.08)]">
            <iframe
              title="Bloc & Brush Office — Dubai"
              src="https://www.google.com/maps?q=Business+Bay+Dubai&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-[420px] border-0 grayscale-[0.1]"
              data-testid="contact-google-map"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-[#F5F5F5]" data-testid="contact-faq">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">FAQs</span>
            <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
              Frequently Asked
              <br />
              <span className="text-[#13294B]">Questions.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-12">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`cfaq-${i}`} data-testid={`contact-faq-${i}`} className="border-b border-black/10">
                  <AccordionTrigger className="text-left font-display text-[17px] lg:text-[19px] font-semibold text-[#1C1C1C] py-6 hover:no-underline hover:text-[#13294B]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-[1.8] text-[#1C1C1C]/70 pb-6 pr-8">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-[#13294B] text-white" data-testid="contact-final-cta">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=2000&q=80)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1A33] via-[#0B1A33]/85 to-[#13294B]/70" />
        <div className="relative container mx-auto px-6 lg:px-12 max-w-7xl py-24 lg:py-32">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.22em] text-white/55">Ready when you are</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] font-extrabold leading-[1.05] tracking-tight mt-5">
              Ready To Transform
              <br />
              <span className="text-white/65">Your Property?</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-xl leading-relaxed">
              Whether you're planning a complete renovation, a commercial fit-out,
              holiday home furnishing or professional cleaning, our team is
              ready to deliver exceptional results.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a href="#contact-form" data-testid="final-cta-quote" className="inline-flex items-center justify-center gap-2 bg-white text-[#13294B] hover:bg-white/90 rounded-full px-7 py-4 text-[15px] font-medium transition">
                Request Free Quote <ArrowRight size={16} />
              </a>
              <a href={BRAND.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] rounded-full px-7 py-4 text-[15px] font-medium transition">
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a href={BRAND.phoneHref} data-testid="final-cta-call" className="inline-flex items-center justify-center gap-2 border border-white/25 hover:bg-white/10 rounded-full px-7 py-4 text-[15px] font-medium transition">
                <Phone size={16} /> Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer onQuoteClick={() => setQuoteOpen(true)} />
      <WhatsAppFloat />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-[11px] uppercase tracking-[0.18em] text-[#707070] font-medium">
      {label}
    </Label>
    {children}
  </div>
);

const SelectField = ({ value, onChange, options, placeholder, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-11">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o} value={o}>{o}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);
